import { faAngleDown, faListUl, faPlus } from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';
import { fetchWaitlistsThunk, selectWaitlist, showWaitlistForm } from 'actions/waitlists/WaitlistsActions';
import { DrawerBody } from 'components/UI/V3/Drawer/Body/DrawerBody';
import { DrawerHeader } from 'components/UI/V3/Drawer/Header/DrawerHeader';
import { Tabs } from 'components/UI/V2/Tabs/Tabs';
import Button from 'components/UI/V3/Button/Button';
import { Dropdown, DropdownContent, DropdownTrigger } from 'components/UI/V3/Dropdown';
import { DropdownItem } from 'components/UI/V3/Dropdown/Item/DropdownItem';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import Spinner from 'components/UI/V3/Spinner';
import WaitlistCard from 'components/Waitlist/WaitlistCard/WaitlistCard';
import {
    getWaitlistCount,
    WAITLIST_SERVICE_KEY_ICONS,
    WAITLIST_SERVICE_KEY_LABELS,
    WAITLIST_VIEW_LABELS
} from 'components/Waitlist/waitlistUtils';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import {
    WAITLIST_SERVICE_KEYS,
    WAITLIST_VIEWS,
    Waitlist,
    WaitlistCounts,
    WaitlistKey,
    WaitlistsPagination,
    WaitlistView
} from 'model/Waitlist';
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { useDebounce } from 'use-debounce';
import styles from './WaitlistList.module.scss';

interface WaitlistListProps {
    initialKey: WaitlistKey;
    marketplaceIds: Array<number>;
}

// Drawer z-index is 10002: dropdowns inside it must render above.
const DROPDOWN_Z_INDEX = 10010;

export const WaitlistList: FunctionComponent<WaitlistListProps> = ({ initialKey, marketplaceIds }) => {
    const dispatch = useDispatch();

    const { getOrganizationServiceCategories } = useOrganizationMarketplaces();

    const waitlists = useSelector<RootState, Array<Waitlist>>(state => state.waitlists.waitlists);
    const pagination = useSelector<RootState, WaitlistsPagination | undefined>(state => state.waitlists.pagination);
    const counts = useSelector<RootState, WaitlistCounts | undefined>(state => state.waitlists.counts);
    const loading = useSelector<RootState, boolean>(state => state.waitlists.loading);
    const listVersion = useSelector<RootState, number>(state => state.waitlists.listVersion);

    const [view, setView] = useState<WaitlistView>('latest');
    const [waitlistKey, setWaitlistKey] = useState<WaitlistKey>(initialKey);
    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebounce(search, 500);

    // True from a filter change until its first page arrives, so stale results are not shown.
    const [reloading, setReloading] = useState(true);

    const sentinelRef = useRef<HTMLDivElement>(null);

    const serviceKeys = useMemo(() => {
        const organizationCategories = getOrganizationServiceCategories();
        return WAITLIST_SERVICE_KEYS.filter(key => organizationCategories.includes(key));
    }, []);

    const multipleLocations = marketplaceIds.length > 1;

    const filter = useMemo(
        () => ({ view, key: waitlistKey, search: debouncedSearch, marketplaceIds }),
        [view, waitlistKey, debouncedSearch, marketplaceIds.join(',')]
    );

    useEffect(() => {
        setReloading(true);
        dispatch(fetchWaitlistsThunk(filter));
    }, [filter, listVersion]);

    useEffect(() => {
        if (!loading) {
            setReloading(false);
        }
    }, [loading]);

    const hasMore = pagination !== undefined && pagination.page < pagination.totalPages;

    useEffect(() => {
        const sentinel = sentinelRef.current;

        if (!sentinel || !hasMore || loading) {
            return;
        }

        const observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting) && pagination) {
                dispatch(fetchWaitlistsThunk(filter, pagination.page + 1));
            }
        });

        observer.observe(sentinel);

        return () => observer.disconnect();
    }, [hasMore, loading, pagination?.page, filter]);

    // Latest and Oldest include the same records (Active + Expired), only the order changes.
    const unresolvedCount = getWaitlistCount(counts, waitlistKey);
    const expiredCount = getWaitlistCount(counts?.expired, waitlistKey);

    const tabs = WAITLIST_VIEWS.map(id => ({
        id,
        name: WAITLIST_VIEW_LABELS[id],
        count: id === 'expired' ? expiredCount : unresolvedCount
    }));

    const serviceFilterLabel = waitlistKey === 'all' ? 'All Services' : WAITLIST_SERVICE_KEY_LABELS[waitlistKey];

    const serviceFilter = (
        <Dropdown>
            <DropdownTrigger className={styles.serviceTrigger}>
                <Button
                    variant="outlined"
                    size="medium"
                    leftIcon={faListUl}
                    rightIcon={faAngleDown}
                    label={serviceFilterLabel}
                />
            </DropdownTrigger>
            <DropdownContent
                zIndex={DROPDOWN_Z_INDEX}
                minWidth={180}
                align="start"
                className={styles.serviceMenu}
            >
                <DropdownItem
                    leftIcon={faListUl}
                    className={clsx({ [styles.selectedService]: waitlistKey === 'all' })}
                    onSelect={() => setWaitlistKey('all')}
                >
                    All Services
                </DropdownItem>
                {serviceKeys.map(key => (
                    <DropdownItem
                        key={key}
                        leftIcon={WAITLIST_SERVICE_KEY_ICONS[key]}
                        className={clsx({ [styles.selectedService]: waitlistKey === key })}
                        onSelect={() => setWaitlistKey(key)}
                    >
                        {WAITLIST_SERVICE_KEY_LABELS[key]}
                    </DropdownItem>
                ))}
            </DropdownContent>
        </Dropdown>
    );

    return (
        <>
            <DrawerHeader
                title="Waitlist"
                actions={
                    <Button
                        variant="green"
                        size="medium"
                        leftIcon={faPlus}
                        label="Add"
                        onClick={() => dispatch(showWaitlistForm())}
                    />
                }
            />

            <DrawerBody className={styles.body}>
                {serviceFilter}

                <div className={styles.searchBox}>
                    <SearchBar
                        id="waitlist-search"
                        searchText={search}
                        placeholder="Search by client name"
                        onChangeSearchText={setSearch}
                    />
                </div>

                <Tabs
                    value={view}
                    tabs={tabs}
                    onChange={id => setView(id as WaitlistView)}
                />

                {reloading && (
                    <div className={styles.spinner}>
                        <Spinner size={32} />
                    </div>
                )}

                {!reloading && waitlists.length === 0 && (
                    <p className={styles.empty}>
                        {debouncedSearch.trim().length > 0
                            ? 'No waitlist requests match your search.'
                            : 'There are no waitlist requests here yet.'}
                    </p>
                )}

                {!reloading && waitlists.length > 0 && (
                    <div className={styles.list}>
                        {waitlists.map(waitlist => (
                            <WaitlistCard
                                key={waitlist.id}
                                waitlist={waitlist}
                                showLocation={multipleLocations}
                                onClick={() => dispatch(selectWaitlist(waitlist.id))}
                            />
                        ))}

                        {hasMore && (
                            <div
                                ref={sentinelRef}
                                className={styles.spinner}
                            >
                                {loading && <Spinner size={24} />}
                            </div>
                        )}
                    </div>
                )}
            </DrawerBody>
        </>
    );
};

export default WaitlistList;
